import { SlidersHorizontal } from "lucide-react";
import { Button, ButtonProps } from "../ui/button";
import { BasicTooltip } from "../ui/Tooltip";
import { useState, forwardRef, ComponentRef } from "react";

const PROVIDER_ICON_SLUGS: Record<string, string> = {
  openai: "openai",
  anthropic: "anthropic",
  google: "google",
  azure: "azure",
  together: "together",
  anyscale: "anyscale",
  huggingface: "huggingface",
  cohere: "cohere",
  gemini: "gemini",
  mistral: "mistral",
  fireworks: "fireworks",
  groq: "groq",
  llama: "llama",
  settings: "settings",
};

const COMMON_HOVER_EFFECT = "hover:bg-gray-100 dark:hover:bg-gray-800";

interface ModelNameButtonProps extends ButtonProps {
  readonly?: boolean;
  iconLoadError?: boolean;
  iconSlug?: string;
  providerDisplay?: string;
  modelName?: string;
  tooltipContent?: string;
  provider?: string;
  label?: string;
  logo?: React.ReactNode;
  asButton?: boolean;
  functionName?: string;
  showTooltip?: boolean;
}

export const ModelNameButton = forwardRef<
  ComponentRef<typeof Button>,
  ModelNameButtonProps
>(
  (
    {
      readonly = false,
      provider,
      modelName,
      providerDisplay,
      tooltipContent,
      label,
      logo,
      asButton = true,
      functionName,
      showTooltip = true,
      ...props
    },
    ref,
  ) => {
    const [iconLoadError, setIconLoadError] = useState(false);
    const iconSlug = PROVIDER_ICON_SLUGS[provider || ""] || "settings";
    const FallbackIcon = SlidersHorizontal;
    const displayLabel = label || modelName;

    const tooltipText = modelName
      ? `${providerDisplay}: ${modelName}`
      : providerDisplay;

    if (!asButton || functionName) {
      return (
        <BasicTooltip
          content={tooltipContent || tooltipText}
          shouldShow={showTooltip}
        >
          <div className="flex items-center whitespace-nowrap">
            {iconLoadError ? (
              <FallbackIcon className="mr-2 h-4 w-4 transition-colors" />
            ) : (
              <img
                src={`https://unpkg.com/@lobehub/icons-static-svg@latest/icons/${iconSlug}.svg`}
                alt={`${providerDisplay} icon`}
                className={`mr-1 h-4 w-4 transition-colors ${
                  functionName ? "mr-2 h-6 w-6" : ""
                }`}
                onError={() => setIconLoadError(true)}
              />
            )}
            <div className="flex flex-col">
              <span className="max-w-[250px] truncate text-base font-medium">
                {displayLabel}
              </span>
              <span className="text-sm text-gray-500">{functionName}</span>
            </div>
          </div>
        </BasicTooltip>
      );
    }

    const hoverEffect = COMMON_HOVER_EFFECT;

    return (
      <BasicTooltip shouldShow={!label} content={tooltipContent || tooltipText}>
        <Button
          ref={ref}
          variant="ghost"
          className={`${
            props.className || ""
          } group relative flex transition-all duration-200 ${hoverEffect}`}
          {...props}
        >
          <div className="flex flex-row items-center gap-1">
            <div className="relative">
              {!iconLoadError && !logo ? (
                <div className="transform transition-transform duration-200 group-hover:scale-110 group-active:scale-90">
                  {iconLoadError ? (
                    <FallbackIcon className="mr-2 h-4 w-4 transition-colors" />
                  ) : (
                    <img
                      src={`https://unpkg.com/@lobehub/icons-static-svg@latest/icons/${iconSlug}.svg`}
                      alt={`${providerDisplay} icon`}
                      className={` mr-1 h-4 w-4 transition-colors`}
                      onError={() => setIconLoadError(true)}
                    />
                  )}
                </div>
              ) : (
                logo ?? null
              )}
            </div>
            <span className="flex items-center whitespace-nowrap">
              {displayLabel ? (
                <span className="max-w-[150px] truncate text-sm font-medium">
                  {displayLabel}
                </span>
              ) : (
                <span className="text-gray-500">Select model...</span>
              )}
            </span>
          </div>
        </Button>
      </BasicTooltip>
    );
  },
);
